import { createUseStyles } from "react-jss";
import * as Mixins from "../../../theme/Mixins";

// These used `||`, so "Squared" (0) and "no borders" (0) were silently coerced to 4px and 1px in both
// themes. `??` keeps the fallback for a genuinely absent value while honouring an explicit 0.
const resolveDropdownBorderRadius = (theme) => theme?.BorderRadius ?? 4;
const resolveDropdownBorderWidth = (theme) => theme?.BorderWidth ?? 1;

// Safe color helper with fallbacks
const safeAlpha = (color, alpha) => {
    try {
        if (!color) return `rgba(255, 255, 255, ${alpha})`;
        return Mixins.alpha(color, alpha);
    } catch (e) {
        console.warn('Color alpha error:', e, 'color:', color, 'alpha:', alpha);
        return `rgba(255, 255, 255, ${alpha})`;
    }
};

const safeDarken = (color, amount) => {
    try {
        if (!color) return '#cccccc';
        return Mixins.darken(color, amount);
    } catch (e) {
        console.warn('Color darken error:', e, 'color:', color);
        return '#cccccc';
    }
};

const useStyles = createUseStyles((isMobile: boolean) => ({
    dropdownContainer: ({ isMobile }) => ({
        position: "relative",
        width: "100%",
        maxWidth: isMobile ? "100%" : "220px",
    }),

    dropdownSelect: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0px 8px",
        maxWidth: isMobile ? "100%" : "300px",
        backgroundColor: ({ theme }) => safeAlpha(theme?.ControlBackground, theme?.ControlAlpha), // this will be changed once we add new theme changes for text highlighter question
        border: ({ theme }) => `${resolveDropdownBorderWidth(theme)}px solid ${theme?.ControlBorderColor || '#cccccc'}`,
        borderRadius: ({ theme }) => resolveDropdownBorderRadius(theme),
        cursor: "pointer",
        fontSize: "14px",
        fontFamily: ({ theme }) => `"${theme?.TextFont || 'Arial'}", sans-serif`,
        transition: "all 0.2s ease",
        outline: "none",
        minHeight: "27px",

        "&:hover": {
            borderColor: ({ theme }) => safeDarken(theme?.ControlBorderColor || '#cccccc', 0.2),
            // Hover always shows the configured background color at full opacity (light grey on new
            // themes) as a visible hover affordance, independent of the resting-state ControlAlpha.
            backgroundColor: ({ theme }) => safeAlpha(theme?.ControlBackground, 1),
        },

        "&:focus": {
            borderColor: ({ theme }) => theme?.PrimaryColor || '#007bff',
        }
    },

    dropdownSelectHasValue: {
        color: ({ theme }) => theme?.TextColor || '#000000',
    },

    dropdownSelectError: {
        borderColor: ({ theme }) => `${theme?.ErrorColor || '#F0345C'} !important`,
        color: ({ theme }) => `${theme?.ErrorColor || '#F0345C'} !important`,
    },

    dropdownText: {
        flex: 1,
        textAlign: "left",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },

    dropdownIcon: {
        marginLeft: "8px",
        fontSize: "14px",
        color: ({ theme }) => safeAlpha(theme?.TextColor || '#000000', 0.6),
        transition: "transform 0.2s ease",
    },

    // Dropdown container styles
    dropdownList: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        position: "absolute",
        left: 0,
        right: 0,
        backgroundColor: ({ theme }) => theme?.Background || '#ffffff',
        border: ({ theme }) => `${resolveDropdownBorderWidth(theme)}px solid ${theme?.ControlBorderColor || '#cccccc'}`,
        borderRadius: ({ theme }) => resolveDropdownBorderRadius(theme),
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)",
        zIndex: 1000,
        maxHeight: "300px",
        overflowY: "auto",
        padding: "8px",
    },

    dropdownListTop: {
        bottom: "100%",
        marginBottom: "4px",
    },

    dropdownListBottom: {
        top: "100%",
        marginTop: "4px",
    },

    choiceButton: {
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        padding: "4px",
        width: "max-content",
        borderRadius: "2px",
        backgroundColor: ({ theme }) => theme?.Background || '#ffffff',
        transition: "all 0.2s ease",
        "&:hover": {
            opacity: "0.9",
        },
    },

    choiceContent: {
        flex: 1,
        pointerEvents: 'none',
    },

}));

export default useStyles;
